import {
    Checkbox,
    FormControl,
    FormControlLabel,
    FormHelperText,
    InputLabel,
    MenuItem,
    Select,
    TextField,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers'
import dayjs from 'dayjs'
import { Field, FieldRenderProps, FormRenderProps } from 'react-final-form'
import { NumericFormat, PatternFormat } from 'react-number-format'

export const singleSelectCheckboxUtil = (
    form: FormRenderProps['form'],
    targetFields: string[],
    thisFieldName: string,
) => {
    targetFields.map((field) => form.change(field, false))
    form.change(thisFieldName, true)
}

export type DropdownValues = { key: string; value: string }[]
type FieldType = 'text' | 'checkbox' | 'date' | 'number' | 'dropdown' | 'phone' | 'currency'

interface MultistepFromFieldProps {
    name: string
    label?: string
    type?: FieldType
    inputProps?: any
    dropdownValues?: DropdownValues
}

const MultistepFormField = (props: MultistepFromFieldProps) => {
    const renderInput = (fieldProps: FieldRenderProps<any, any, any>) => {
        const error = fieldProps.meta.submitError ?? fieldProps.meta.error
        const showError =
            (fieldProps.meta.error && fieldProps.meta.modified) ||
            (fieldProps.meta.submitError !== undefined && fieldProps.meta.submitError !== '')

        switch (props.type) {
            case 'checkbox':
                return (
                    <FormControl error={showError} {...props.inputProps}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    name={fieldProps.input.name}
                                    checked={fieldProps.input.value}
                                    onChange={fieldProps.input.onChange}
                                    {...props.inputProps}
                                />
                            }
                            label={props.label}
                        />
                        {showError && <FormHelperText>{error}</FormHelperText>}
                    </FormControl>
                )
            case 'date': {
                const { value } = fieldProps.input
                return (
                    <FormControl error={showError} {...props.inputProps}>
                        <DatePicker
                            label={props.label}
                            value={value?.length ? dayjs(value) : null}
                            onChange={fieldProps.input.onChange}
                            {...props.inputProps}
                        />
                        {showError && <FormHelperText>{error}</FormHelperText>}
                    </FormControl>
                )
            }
            case 'number':
                return (
                    <NumericFormat
                        value={fieldProps.input.value}
                        onChange={fieldProps.input.onChange}
                        customInput={TextField}
                        {...{
                            error: showError,
                            helperText: showError && error,
                            label: props.label,
                            ...props.inputProps,
                        }}
                    />
                )
            case 'currency':
                return (
                    <NumericFormat
                        value={fieldProps.input.value}
                        thousandSeparator
                        valueIsNumericString
                        prefix="$"
                        onValueChange={({ value }) => fieldProps.input.onChange(value)}
                        customInput={TextField}
                        {...{
                            error: showError,
                            helperText: showError && error,
                            label: props.label,
                            ...props.inputProps,
                        }}
                    />
                )
            case 'dropdown':
                return (
                    <FormControl error={showError} {...props.inputProps}>
                        {props.label && <InputLabel>{props.label}</InputLabel>}
                        <Select
                            onChange={fieldProps.input.onChange}
                            value={fieldProps.input.value ?? []}
                            sx={{ height: '40px' }}
                            {...props.inputProps}
                        >
                            {props.dropdownValues?.map(({ key, value }) => (
                                <MenuItem key={key} value={key}>
                                    {value}
                                </MenuItem>
                            ))}
                        </Select>
                        {showError && <FormHelperText>{error}</FormHelperText>}
                    </FormControl>
                )
            case 'phone':
                return (
                    <PatternFormat
                        format="+1 (###) ###-####"
                        value={fieldProps.input.value}
                        onChange={fieldProps.input.onChange}
                        customInput={TextField}
                        {...{
                            error: showError,
                            helperText: showError && error,
                            label: props.label,
                            ...props.inputProps,
                        }}
                    />
                )
            case 'text':
            default:
                return (
                    <TextField
                        sx={{ width: '100%' }}
                        label={props.label}
                        name={fieldProps.input.name}
                        value={fieldProps.input.value}
                        onChange={fieldProps.input.onChange}
                        error={showError}
                        helperText={showError && error}
                        {...props.inputProps}
                    />
                )
        }
    }

    return <Field name={props.name}>{renderInput}</Field>
}

export default MultistepFormField
